import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Search, ArrowRight, CheckCircle2, Package, Truck, MapPin } from "lucide-react";

const STATUS_ORDER = ["draft", "approved", "loaded", "in_transit", "delivered", "closed"];
const STATUS_LABEL = {
  draft: "Order Received",
  approved: "Approved",
  loaded: "Loaded",
  in_transit: "In Transit",
  delivered: "Delivered",
  closed: "Closed",
  cancelled: "Cancelled",
};

export default function TrackPage() {
  const { cn } = useParams();
  const [q, setQ] = useState(cn || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async (value) => {
    if (!value) return;
    setBusy(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${backend}/api/track/${encodeURIComponent(value.toUpperCase().trim())}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Consignment not found");
      setData(null);
    } finally { setBusy(false); }
  };

  useState(() => { if (cn) load(cn); });

  const onSubmit = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/track/${encodeURIComponent(q.toUpperCase().trim())}`); load(q); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#2A2A2A]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="text-sm text-zinc-400 hover:text-white">Ops Login</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="overline text-[#FF5A00]">SHIPMENT TRACKING</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight" data-testid="track-page">
          Where&apos;s my load?
        </h1>
        <p className="mt-4 text-zinc-400">Enter your NorthLine consignment number (starts with <span className="text-[#FF5A00] font-mono-ll">NL-</span>).</p>

        <form onSubmit={onSubmit} className="mt-8 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="track-input"
              placeholder="NL-2608-00001"
              className="h-12 pl-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00] font-mono-ll" />
          </div>
          <Button type="submit" disabled={busy} data-testid="track-submit-btn"
            className="h-12 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold px-6">
            {busy ? "Searching..." : "Track"}
          </Button>
        </form>

        {data && (
          <div className="mt-10 space-y-6" data-testid="track-result">
            {data.pod && (
              <div className={`p-4 border rounded-sm ${data.pod.outcome === "delivered_ok" ? "border-emerald-500/40 bg-emerald-500/10" : data.pod.outcome === "short_delivery" ? "border-amber-500/40 bg-amber-500/10" : "border-rose-500/40 bg-rose-500/10"}`} data-testid="track-pod-banner">
                <div className="text-xs uppercase tracking-widest">POD OUTCOME</div>
                <div className="font-display font-black text-xl mt-1">
                  {data.pod.outcome === "delivered_ok" ? "Delivered — OK" : data.pod.outcome === "short_delivery" ? "Delivered — Short" : "Delivered — Damaged"}
                </div>
                {data.pod.remarks && <div className="text-sm text-zinc-300 mt-1">{data.pod.remarks}</div>}
                <div className="text-[10px] text-zinc-500 mt-2">Recorded {data.pod.recorded_at ? new Date(data.pod.recorded_at).toLocaleString("en-IN") : ""}</div>
              </div>
            )}
            {data.has_pod_photo && data.pod_photo_url && (
              <div className="p-4 border border-[#2A2A2A] bg-[#141414]" data-testid="track-pod-photo-block">
                <div className="overline text-zinc-500 mb-3">Proof of Delivery photo</div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${data.pod_photo_url}`} target="_blank" rel="noopener noreferrer">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${data.pod_photo_url}`}
                    alt={`POD for ${data.cn_number}`}
                    data-testid="track-pod-photo"
                    className="max-h-96 w-auto rounded-sm border border-[#2A2A2A] object-contain bg-white" />
                </a>
                <div className="text-[10px] text-zinc-500 mt-2">Tap image to open in full size.</div>
              </div>
            )}
            <div className="p-6 border border-[#2A2A2A] bg-[#141414]">
              <div className="flex flex-wrap justify-between gap-3 items-start">
                <div>
                  <div className="overline text-[#FF5A00]">CN NUMBER</div>
                  <div className="font-display font-black text-2xl mt-1 font-mono-ll">{data.cn_number}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-zinc-500">CURRENT STATUS</div>
                  <div className="font-display font-black text-xl mt-1 text-[#FF5A00]">{STATUS_LABEL[data.status] || data.status.toUpperCase()}</div>
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <Field label="Consignor" value={data.consignor || data.customer_name} />
                <Field label="Consignee" value={data.consignee || "—"} />
                <Field label="From" value={data.source_city} icon={MapPin} />
                <Field label="To" value={data.destination_city} icon={MapPin} />
                <Field label="Material" value={data.material || "—"} />
                <Field label="Weight" value={data.weight_kg ? `${data.weight_kg} kg` : "—"} />
                <Field label="Vehicle" value={data.vehicle_number || "—"} icon={Truck} />
                <Field label="Driver" value={data.driver_name || "—"} />
              </div>
            </div>

            {/* Timeline */}
            <div className="p-6 border border-[#2A2A2A] bg-[#141414]">
              <div className="overline text-zinc-500 mb-4">Movement Timeline</div>
              <div className="relative pl-6">
                <div className="absolute left-2 top-1 bottom-1 w-px bg-[#2A2A2A]"></div>
                {(data.history || []).map((h, i) => (
                  <div key={i} className="relative pb-4">
                    <div className="absolute -left-6 top-1 w-3.5 h-3.5 rounded-full bg-[#FF5A00] shadow-[0_0_0_4px_rgba(255,90,0,0.15)]"></div>
                    <div className="font-semibold text-sm">{STATUS_LABEL[h.status] || h.status}</div>
                    <div className="text-xs text-zinc-500 font-mono-ll mt-0.5">{h.at ? new Date(h.at).toLocaleString("en-IN") : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, icon: I }) {
  return (
    <div className="flex items-start gap-3">
      {I ? <I className="w-4 h-4 text-[#FF5A00] mt-0.5" /> : <div className="w-4"></div>}
      <div>
        <div className="overline text-zinc-500 text-[10px]">{label}</div>
        <div className="text-zinc-200">{value}</div>
      </div>
    </div>
  );
}
